import React, { useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import {
  oneLight,
  nord,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import "./CopyCode.css";
import { useAppSelector } from "../../../../store/hooks";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useGetAttachmentLink } from "../../../../store/Apis/ChatModelApi";
import { setSnackBarAlert } from "../../../../store/one-query/chatsSlice";
import { useDispatch } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

interface Props {
  response: string;
  currentMessageId?: string;
}
interface SandboxLinkProps {
  link: string;
  text: string;
}
const ChatResponseFormat = (props: Props) => {
  const { response, currentMessageId } = props;
  const [copiedBlock, setCopiedBlock] = useState<number | null>(null);
  const [sandboxLinks, setSandboxLinks] = useState<SandboxLinkProps[]>();
  const { mutate: getAttachmentLink } = useGetAttachmentLink();
  let darkMode = useAppSelector((state) => state.User.darkMode);
  const dispatch = useDispatch();
 
  useEffect(() => {
    const regex = /\[([^\]]+)\]\((sandbox:[^\)]+)\)/g;
    let matches;
    const result: SandboxLinkProps[] = [];
    while ((matches = regex.exec(response)) !== null) {
      const text = matches[1];
      const filePath = matches[2];
      result.push({ text, link: filePath });
    }
    setSandboxLinks(result as SandboxLinkProps[]);
  }, [response]);
  
  const createDownloadLink = (data: string) => {
    const link = document.createElement("a");
    link.href = data;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showErrorMessage = () => {
    dispatch(
      setSnackBarAlert({
        show: true,
        message: "Please try again later.",
        alertType: "error",
      })
    );
  };

  const handleDownloadFile = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    sandboxLink: string
  ) => {
    event.preventDefault();
    const linkRequest = {
      url: sandboxLink,
      messageId: currentMessageId ?? "",
    };
    getAttachmentLink(linkRequest, {
      onSuccess: (data) => {
        if (data) createDownloadLink(data);
      },
      onError: () => {
        showErrorMessage();
      },
    });
  };
  const handleCopy = (index: number, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedBlock(index);
    setTimeout(() =>setCopiedBlock(null), 2500);
  };
  return (
    response && (
      <div className="containerChatFormat mt-2">
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ className, children ,node}: any) {
              const isInline = !className;
              if (isInline) {
                return (
                  <SyntaxHighlighter
                    style={darkMode ? nord : oneLight}
                    language="javascript"
                    customStyle={{
                      display: "inline",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    {String(children).trim()}
                  </SyntaxHighlighter>
                );
              }
              const match = /language-(\w+)/.exec(className || "");
              const blockIndex = node.position?.start.offset || Math.random(); // Ensures uniqueness
              return match ? (
                <div className="divParent">
                  <hr className="horizentalLine chat-prompt" />
                  <div className="programmingLanguageName">{match[1]}</div>
                  <div className="copyCode">
                    <button
                      className="buttonCopy"
                      onClick={() =>
                        handleCopy(blockIndex, String(children).trim())
                      }
                    >
                      {copiedBlock !== blockIndex ? (
                        <>
                          <ContentCopyIcon
                            sx={{
                              width: "16px !important",
                              height: "16px",
                              color: "var(--Main_icons_color)",
                            }}
                          />
                          <span className="ms-1"> Copy</span>
                        </>
                      ) : (
                        <>
                          <DoneIcon
                            sx={{
                              width: "16px",
                              height:"16px",
                              color: "#12B6EA",
                            }}
                          />
                          <span className="ms-1 copied-code">Copied!</span>
                        </>
                      )}
                    </button>
                  </div>

                  <SyntaxHighlighter
                    customStyle={{
                      backgroundColor:"var(--A_background77)",
                      borderTop: "35px solid var(--A_background77)",
                      borderRadius: "0.375rem",
                      fontSize: "14px",
                      zIndex: "2",
                      marginTop: "30px",
                    }}
                    style={darkMode ? nord : oneLight}
                    language={match[1]}
                    children={String(children)?.replace(/\n$/, "")}
                  ></SyntaxHighlighter>
                </div>
              ) : (
                <code className={className ? className : ""}>{children}</code>
              );
            },
            a({ href, children, ...props }) {
              if (!href && !sandboxLinks?.length) {
                return null; // Hide anchor tags with empty href
              }
              if (href)
                return (
                  <a href={href} {...props} target={"_blank"}>
                    {children}
                  </a>
                );
              if (sandboxLinks && sandboxLinks?.length > 0) {
                const url = sandboxLinks?.find(
                  (sandboxLink) => children === sandboxLink.text
                );
                return (
                  <a
                    href="#"
                    onClick={(e) => {
                      handleDownloadFile(e, url?.link ?? "");
                    }}
                  >
                    {url?.text}
                  </a>
                )};
              },
            table({ children }) {
              return (
                <TableContainer component={Paper} className="table-container mt-2 mb-2">
                  <Table>{children}</Table>
                </TableContainer>
              );
            },
            thead({ children }) {
              return <TableHead className="table-head-format">{children}</TableHead>;
            },
            tbody({ children }) {
              return <TableBody className="table-body-format">{children}</TableBody>;
            },
            tr({ children }) {
              return <TableRow>{children}</TableRow>;
            },
            th({ children }) {
              return <TableCell style={{ fontWeight: "bold" }}>{children}</TableCell>;
            },
            td({ children }) {
              return <TableCell>{children}</TableCell>;
            },
          }}
        >
          {response}
        </Markdown>
      </div>
    )
  );
};

export default ChatResponseFormat;